import { SYMPTOM_BASE_URL } from "@qendo/medical";
import { MedicalType } from "@qendo/medical/types";
import { OptionType } from "@qendo/types";
import type { DateArg } from "date-fns";
import { router } from "expo-router";
import { useEffect, useId, useState } from "react";

import { Button } from "~/components/Button";
import { OptionSelector } from "~/components/form/combo-box/OptionSelector";
import { DatePicker } from "~/components/form/date-picker/DatePicker";
import { FieldGroup } from "~/components/form/FieldGroup";
import { MediaPicker } from "~/components/form/gallery/MediaPicker";
import { TextArea } from "~/components/form/TextArea";
import { FullPageModal, type FullPageModalProps } from "~/components/layout/modal/FullPageModal";
import { Space } from "~/constants/sizing";
import type { PastSymptom } from "~/entities/medical/past_symptom";
import { useFeedback } from "~/providers/FeedbackProvider";
import { useMedical } from "~/providers/MedicalProvider";
import { elementsMatch } from "~/util/array";

type PastSymptomFormProps = {
    readonly symptom?: PastSymptom;
    onRequestClose: () => void;
} & FullPageModalProps;

export const PastSymptomForm = ({ symptom, onRequestClose, ...modalProps }: PastSymptomFormProps) => {
    const { canUpdateMedical, storeMedical } = useMedical();
    const { saving, setSaving } = useFeedback();
    const [option, setOption] = useState<string | null>(symptom?.option_id ?? null);
    const [occurredOn, setOccurredOn] = useState<DateArg<Date> | null>(symptom?.occurred_on ?? null);
    const [description, setDescription] = useState(symptom?.description ?? "");
    const [photographs, setPhotographs] = useState<string[]>(symptom?.photograph_ids ?? []);
    const [dirty, setDirty] = useState(false);
    const id = useId();

    useEffect(() => {
        setDirty(
            (isDirty) =>
                isDirty ||
                option !== (symptom?.option_id ?? null) ||
                occurredOn !== (symptom?.occurred_on ?? null) ||
                description !== (symptom?.description ?? "") ||
                !elementsMatch(photographs, symptom?.photograph_ids ?? []),
        );
    }, [symptom, option, description, occurredOn, photographs]);

    const canSave = canUpdateMedical(MedicalType.past_symptom) && !saving && option !== null && occurredOn !== null;

    const doSave = () => {
        if (!canSave) {
            return;
        }

        setSaving(id);
    };

    const commit = () => {
        storeMedical({
            type: MedicalType.past_symptom,
            id: symptom?.id,
            option_id: option,
            photograph_ids: photographs,
            occurred_on: occurredOn,
            description,
        })
            .then((id) => {
                id &&
                    setTimeout(
                        () => router.dismissTo({ pathname: `${SYMPTOM_BASE_URL}/[id]/show`, params: { id } }),
                        10,
                    );
            })
            .finally(() => setSaving(false));
    };

    const is_saving = saving === id;

    // biome-ignore lint/correctness/useExhaustiveDependencies(commit): callback
    useEffect(() => {
        is_saving && commit();
    }, [is_saving]);

    return (
        <FullPageModal
            {...modalProps}
            dirty={dirty}
            title={`${symptom?.id ? "Edit" : "Add"} past symptom`}
            cancelable
            headerRight={() => (
                <Button disabled={!canSave} title="Save" size="sm" onPress={doSave} loading={is_saving} />
            )}
            onRequestClose={onRequestClose}
            gap={Space.lg}
        >
            <FieldGroup>
                <OptionSelector
                    type={OptionType.past_symptoms}
                    value={option}
                    onItemSelected={setOption}
                    label="Symptom"
                    disabled={is_saving}
                    name="option_id"
                />
                <DatePicker
                    value={occurredOn}
                    onDateSelected={setOccurredOn}
                    label="Last occurred"
                    placeholder="Select date"
                    disabled={is_saving}
                    name="occurred_on"
                />
            </FieldGroup>
            <FieldGroup>
                <TextArea
                    value={description}
                    onChangeText={setDescription}
                    label="Description"
                    optional
                    disabled={is_saving}
                    name="description"
                />
                <MediaPicker value={photographs} onSelectionChanged={setPhotographs} disabled={is_saving} />
            </FieldGroup>
        </FullPageModal>
    );
};
